import React from "react"
import { graphql, Link, navigate } from "gatsby"
import { useSiteContext } from "../components/common/SiteContext"
import MainPage from "./MainPage"
import { MarketStatsMarket } from "@organic-return/foundation-react/src/components/MarketStats/MarketStats"
import { CommunityLayoutStandard } from "@organic-return/foundation-react/src/components/Community/CommunityLayoutStandard"
import { IContentCard } from "@organic-return/foundation-react/src/components/Blades/ContentCardsBlade"
import { IFeaturedContentOption } from "@organic-return/foundation-react/src/components/Blades/FeaturedWithDropdownContent"
import axios from "axios"
import { getRecaptchaToken, getSegmentAnonymousId, registerSegmentActivity } from "../lib/utils"
import { IContentSection } from "@organic-return/foundation-react/src/components/Community/util"
import { IEntrySearchProps } from "@organic-return/foundation-react/src/components/Search"
import { IImageWithOverlappedTextBladeProps } from "@organic-return/foundation-react/src/components/Blades/ImageWithOverlappedTextBlade"

export const query = graphql`
  query($nodeID: String!) {
    drupalCommunity(id: { eq: $nodeID }) {
      name
      sEOTitle
      sEODescription
      bladeBGImage {
        width
        url
        height
      }
      image {
        height
        width
        url
      }
      bladeTitle
      description
      highlightedText
      bladeContent
      marketReportBGImage {
        width
        height
        url
      }
      marketReportText
      marketReportCallToAction {
        title
        url
      }
      gallery {
        url
        width
        height
      }
      communityVideo {
        url
      }
      listingsSearchUrl {
        url
      }
      agentsSearchUrl {
        url
      }
      latitude
      longitude
      statsBox {
        title
        content
      }
      amenitiesBGImage {
        url
      }
      amenities {
        title
        content
      }
      statisticsBGImage {
        url
      }
      statistics {
        title
        content
      }
      highlight1Title
      highlight1Content
      highlight2Title
      highlight2Content
      highlight3Title
      highlight3Content
      marketStatsJSON
      lifestyle {
        title
        text
        image {
          url
        }
      }
      contentSections {
        callToAction {
          url {
            title
            url
          }
        }
        content
        image {
          url
        }
        subheader
        title
      }
      neighborhoods {
        name
        path
      }
      complexes {
        name
        path
      }
      listings {
        id
        baths
        bathsHalf
        baths_3_4
        address {
          city
          county
          postal
          text
          street1
          street2
        }
        beds
        lotacres
        majorArea
        minorArea
        features
        flags
        mlsID
        price
        sqft
        status
        statusTS
        subType
        subdivision
        exclusive
        type
        defaultImage {
          dimensions
          url
        }
        path
        location {
          lat
          lng
        }
        hideOnMap
        descriptionShort     
      }      
  }
      config: drupalSiteConfiguration {
        listingsSearchPath      
    }
}
`

const CommunityPage = ({ pageContext, data, location }: any) => {
  const siteContext = useSiteContext()
  const community = data.drupalCommunity

  const Layout = siteContext.communityPageLayout ?? CommunityLayoutStandard

  const pageSize = siteContext.communityLayoutProps?.listingsResultPageSize ?? 20

  const sortByItemOrder = (a: any, b: any) => a.properties.itemOrder - b.properties.itemOrder

  // Small card size option (default)
  let cardClass = "w-[480px]"
  let cardImageWidth = 480
  // Large card size option
  if (community.customFields?.cardSize == "large") {
    cardClass = "w-[640px]"
    cardImageWidth = 640
  }

  let markets: MarketStatsMarket[] | undefined = undefined
  const parsedMarkets = JSON.parse(community.marketStatsJSON)?.markets
  if (parsedMarkets) {
    markets = parsedMarkets
  }

  let communityHighlights: IContentCard[] = []
  if (community.highlight1Content) {
    communityHighlights.push({
      header: community.highlight1Title,
      text: community.highlight1Content
    })
  }
  if (community.highlight2Content) {
    communityHighlights.push({
      header: community.highlight2Title,
      text: community.highlight2Content
    })
  }
  if (community.highlight3Content) {
    communityHighlights.push({
      header: community.highlight3Title,
      text: community.highlight3Content
    })
  }

  let lifestyleItems: IFeaturedContentOption[] | undefined = undefined
  lifestyleItems = community.lifestyle?.map((item: any) => {
    return {
      label: item.title,
      content: item.text,
      image: {
        url: item.image?.url
      }
    }
  })

  let contentSections: IContentSection[] | undefined;
  contentSections = community.contentSections?.map((item: any): IContentSection => {
    return {
      title: item.title,
      content: item.content,
      callsToAction: item.callToAction?.map((cta: any) => {
        return {href: cta.url?.url, text: cta.url?.title};
      }) ?? []
    }
  })

  const activeListings = community.listings?.filter((listing: any) => {
    return listing.status != "Closed"
  })

  const searchProps = siteContext.communityLayoutProps?.listingsSearchProps ?? siteContext.communitySearchProps
  const cardProps = siteContext.communityLayoutProps?.listingsResultCardsProps ?? siteContext.globalListingCardProps

  if (searchProps && !searchProps.mapClick) {
    searchProps.mapClick = item => navigate(item.path)
  }

  let galleryImages: any = []
  if (community.gallery) {
    galleryImages = community.gallery.map((media: any) => {
      return media.url
    })
  }

  const communityRequestInformationClick = async (formInput: any) => {
    const recaptchaToken = await getRecaptchaToken("request_information_community");
    await axios({
      url: process.env.LEAD_CREATE_API_ENDPOINT,
      method: "POST",
      data: {
        recaptchaToken,
        formValues: formInput,
        propertyInfo: {
          createdFrom: `Community Request Information - ${community.customFields?.name}`,
        },
        segmentAnonymousId: getSegmentAnonymousId(),
      },
    })
    registerSegmentActivity(`Community Request Information Form Submission`, formInput)
  }

  if (siteContext!.communityLayoutProps && siteContext!.communityLayoutProps!.contactUsFormProps) {
    siteContext!.communityLayoutProps!.contactUsFormProps!.clickHandler = communityRequestInformationClick;
  }
  if (siteContext!.communityLayoutProps && siteContext!.communityLayoutProps!.requestInfoFormProps) {
    siteContext!.communityLayoutProps!.requestInfoFormProps!.clickHandler = communityRequestInformationClick;
  }

  const marketReportCallToAction = (community.marketReportCallToAction?.url ?
    { href: community.marketReportCallToAction.url, label: community.marketReportCallToAction.title } :
    undefined)
  
  let entrySearchProps: IEntrySearchProps | undefined = undefined;
  if (siteContext.communityLayoutProps?.listingsEntrySearchProps) {
    entrySearchProps = {
      onSubmit: async filters => {
        const params = new URLSearchParams({
          filters: JSON.stringify(filters),
        })
        await navigate(
          data.config.listingsSearchPath + "#" + params.toString() + "&sort=\"priceDESC\"&showMap=true"
        )
      },
      ...siteContext.communityLayoutProps.listingsEntrySearchProps
    }
  }

  let centerBlade: IImageWithOverlappedTextBladeProps | undefined = undefined;
  /*if (community.customFields.centerBladeBackgroundImage?.url &&
    community.customFields.centerBladeForegroundImage?.url &&
    community.customFields.centerBladeTitle &&
    community.customFields.centerBladeContent) {

    const { centerBladeBackgroundImage, centerBladeForegroundImage, centerBladeTitle, centerBladeContent, centerBladeCTA } = community.customFields
    centerBlade = {
      backgroundImage: {
        url: centerBladeBackgroundImage.url,
        dimensions: (centerBladeBackgroundImage.width && centerBladeBackgroundImage.height) && [centerBladeBackgroundImage.width, centerBladeBackgroundImage.height]
      },
      leadImage: {
        url: centerBladeForegroundImage.url,
        dimensions: (centerBladeForegroundImage.width && centerBladeForegroundImage.height) && [centerBladeForegroundImage.width, centerBladeForegroundImage.height]
      },
      title: centerBladeTitle,
      text: centerBladeContent,
      callToAction: centerBladeCTA?.href && centerBladeCTA,
    }
  }*/

  let amenities: IContentSection[] = community.amenities?.map((item: any) => {
    const amenity: IContentSection = {
      title: item.title,
      content: item.content,
    }
    return amenity
  }) ?? []
  

  let statistics: IContentSection[] = community.statistics?.map((item: any) => {
    const statistic: IContentSection = {
      title: item.title,
      content: item.content,
    }
    return statistic
  }) ?? []
  
  let statsBox: IContentSection[] = community.statsBox?.map((item: any) => {
    const stat: IContentSection = {
      title: item.title,
      content: item.content,
    }
    return stat
  }) ?? []
  
  let geoLocation: any = undefined
  if (community?.latitude && community?.longitude) {
    geoLocation = {
      lat: community.latitude,
      lng: community.longitude,
    }
  }

  var pageTitle = community?.sEOTitle;
  var pageDescripton = data.drupalCommunity.sEODescription;
  var pageKeywords = data.druapalCommunity?.metaKeywords;
  
  return (
    <MainPage
      headerProps={{ gradient: true, ...siteContext.headerProps }}
      seoProps={{ title: pageTitle, description: pageDescripton, keywords: pageKeywords, location: location }}
      className="community-page"
    >
      <Layout
        heroBackgroundImageUrl={community.image?.url}
        heroCarouselImagesUrls={galleryImages}
        isTitleInHeading={siteContext.communityLayoutProps?.isTitleInHeading ?? false}
        title={community.name}
        description={community.description}
        highlightedText={community.highlightedText}
        highlights={communityHighlights}
        markets={markets}
        marketReportBgImageUrl={community.marketReportBGImage?.url}
        marketReportText={community.marketReportText}
        marketReportCallToAction={marketReportCallToAction}
        lifestyleItems={lifestyleItems}
        contentSections={contentSections}
        listings={activeListings}
        listingsCardClass={cardClass}
        listingsCardImageWidth={cardImageWidth}
        listingsResultPageSize={pageSize}
        linkFunc={props => {
          return (
            <Link
              to={props.href ?? ""}
              className={`text-inherit ${props.className}`}
              key={props.key}
              title={props.title}
            >
              {props.children}
            </Link>
          )
        }}
        centerBlade={centerBlade}
        listingsSearchUrl={community.listingsSearchUrl?.url}
        agentsSearchUrl={community.agentsSearchUrl?.url}
        listingsSearchProps={searchProps}
        listingsResultCardsProps={cardProps}
        listingsEntrySearchProps={entrySearchProps}
        communityFacts={statsBox}
        communityAmenities={amenities}
        communityAmenitiesBgImageUrl={community.amenitiesBGImage?.url}
        communityStatistics={statistics}
        communityStatisticsBgImageUrl={community.statisticsBGImage?.url}
        location={geoLocation}
        agents={community.agents}
        agentsCardProps={siteContext.globalPeopleSearchCardProps}
        communityVideo={community.communityVideo?.url && {url: community.communityVideo.url}}
        neighborhoods={community.neighborhoods}
        complexes={community.complexes}
        {...siteContext.communityLayoutProps}
      >
      </Layout>
    </MainPage>
  )
}

export default CommunityPage
